import { Injectable, signal, computed } from '@angular/core';
import { Database, ref as dbRef, get as dbGet, update as dbUpdate, onValue, Unsubscribe } from '@angular/fire/database';
import { AppContextServiceService } from './app-context-service.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Printer, ReceiptApiCredentials, WorkingFlags } from '../models/kds.models';
import { innerPrinterAvailable, INTERNAL_PRINTER_ID } from '../utils/sunmi-inner-printer';

export interface SalesCategory {
  isOpen: boolean;
  closedComment: string;
}

export interface SalesContext {
  food: SalesCategory;
  drinks: SalesCategory;
}

const DEFAULT_WORKING_FLAGS: WorkingFlags = {
  soundOnBong: true,
  soundUntilTap: true,
  textOffOnBong: false,
  highlightOnNewOrder:true
};

/**
 * The prep time the pizza voice agent quotes to phone callers, in minutes.
 *
 * The bounds mirror the agent's own guard (pizza-voice-agent
 * src/settings/index.js): outside 5-180 it treats the value as unset and falls
 * back to its 20-25 minute wording rather than saying something absurd aloud.
 * Writing something the agent would refuse would look saved but change nothing,
 * so the KDS clamps to the same range.
 */
export const PREP_MINUTES_MIN = 5;
export const PREP_MINUTES_MAX = 180;
/** The stepper's increment, and where it starts when nothing is stored yet. */
export const PREP_MINUTES_STEP = 5;
export const PREP_MINUTES_DEFAULT = 25;

/** Reads a stored prepMinutes, or null when unset or out of range. Mirrors the agent's readPrepMinutes. */
export function readPrepMinutes(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const minutes = Math.round(Number(raw));
  if (!Number.isFinite(minutes) || minutes < PREP_MINUTES_MIN || minutes > PREP_MINUTES_MAX) return null;
  return minutes;
}

@Injectable({
  providedIn: 'root'
})
export class SettingsServiceService {
  /** Subjects or local cache for settings */
  private _receiptApiCredentials: ReceiptApiCredentials | null = null;
  private _language: string | null = null;
  private routeName: string | null | undefined = null;

  salesContext = signal<SalesContext>({
    food: { isOpen: true, closedComment: '' },
    drinks: { isOpen: true, closedComment: '' }
  });
  readonly hasWeeklyMenuOrdering = signal<boolean>(false);
  isDirty = signal(false);
  savingSales = signal(false);
  private contextUnsubscribe?: Unsubscribe;

  constructor(
    private db: Database,
    private appContextService: AppContextServiceService,
    private http : HttpClient
  ) {
    this.appContextService.selectedUnit$.subscribe((unit) => {
      if (unit) {
        this.routeName = unit;
        this.initAppContextListener(unit);
      }
    });
  }

  private initAppContextListener(unit: string) {
    if (this.contextUnsubscribe) {
      this.contextUnsubscribe();
      this.contextUnsubscribe = undefined;
    }
    const contextRef = dbRef(this.db, `Clients/${unit}/AppInfo/Context`);
    this.contextUnsubscribe = onValue(contextRef, (snap) => {
      const val = snap.val();
      if (!this.isDirty()) {
        this.salesContext.set({
          food: { isOpen: val?.food?.isOpen ?? true, closedComment: val?.food?.closedComment ?? '' },
          drinks: { isOpen: val?.drinks?.isOpen ?? true, closedComment: val?.drinks?.closedComment ?? '' }
        });
      }
    });

    onValue(dbRef(this.db, `adminApp/clients/${unit}/settings/flags/hasWeeklyMenuOrdering`), (snap) => {
      this.hasWeeklyMenuOrdering.set(snap.val() === true);
    });
  }

  async saveSalesContext(): Promise<void> {
    const routeName = this.routeName || this.appContextService.selectedUnit;
    if (!routeName) return;

    this.savingSales.set(true);
    try {
      await dbUpdate(dbRef(this.db, `Clients/${routeName}/AppInfo/Context`), this.salesContext());
      this.isDirty.set(false);
    } finally {
      this.savingSales.set(false);
    }
  }

  resetSalesContext() {
    this.isDirty.set(false);
    if (this.routeName) this.initAppContextListener(this.routeName);
  }

  async init(): Promise<void> {
    this.routeName = this.appContextService.selectedUnit;
    if (!this.routeName) throw new Error('Unit is not selected in AppContext');

    // 1. Get receiptApi credentials from Firebase
    try {
      const receiptApiPath = `adminApp/clients/${this.routeName}/settings/receiptApi`;
      const receiptApiSnap = await dbGet(dbRef(this.db, receiptApiPath));
      const receiptApi = receiptApiSnap.val();
      if (
        receiptApi &&
        receiptApi.salesLocationId &&
        receiptApi.clientGroupId &&
        receiptApi.apiKey
      ) {
        this._receiptApiCredentials = {
          salesLocationId: receiptApi.salesLocationId,
          clientGroupId: receiptApi.clientGroupId,
          apiKey: receiptApi.apiKey,
        };
      } else {
        this._receiptApiCredentials = null;
      }
    } catch (error) {
      this._receiptApiCredentials = null;
    }

    // 2. Get language 
    try {
      const langPath = `adminApp/clients/${this.routeName}/settings/language`;
      const langSnap = await dbGet(dbRef(this.db, langPath));
      this._language = langSnap.val() ?? null;
    } catch (error) {
      this._language = null;
    }

    // 3. Get printers and save/update
    try {
      this.printers = await this.getPrinters();
    } catch (error) {
      this.printers = null;
    }

    // 4. And ask the device whether it has a printer of its own.
    await this.refreshInternalPrinter();
  }
  
  get receiptApiCredentials(): ReceiptApiCredentials | null {
    return this._receiptApiCredentials;
  }
  get language(): string | null {
    return this._language;
  }

  /**
   * The node the pizza voice agent reads its per-restaurant settings from:
   * adminApp/clients/<routeName>/settings/voiceAgent
   *
   * Same root and casing as the receiptApi and language reads above, and the
   * same node the agent subscribes to (its nodeFor() in src/settings/index.js).
   * The KDS writes here; the agent only reads.
   */
  private voiceAgentPath(routeName: string): string {
    return `adminApp/clients/${routeName}/settings/voiceAgent`;
  }

  currentRouteName(): string | null | undefined {
    return this.routeName || this.appContextService.selectedUnit;
  }

  /** The restaurant's prep time, or null when it has never been set. */
  async getPrepMinutes(): Promise<number | null> {
    const routeName = this.currentRouteName();
    if (!routeName) return null;
    try {
      const snap = await dbGet(dbRef(this.db, `${this.voiceAgentPath(routeName)}/prepMinutes`));
      return readPrepMinutes(snap.val());
    } catch (error) {
      return null;
    }
  }

  /**
   * Writes the prep time, clamped to the range the agent accepts.
   *
   * An update on the voiceAgent node rather than a set, so a sibling setting
   * added later (a transfer number, say) is not wiped by a stepper tap.
   */
  async setPrepMinutes(minutes: number): Promise<void> {
    const routeName = this.currentRouteName();
    if (!routeName) throw new Error('No routeName to set prepMinutes.');
    const clamped = Math.min(PREP_MINUTES_MAX, Math.max(PREP_MINUTES_MIN, Math.round(minutes)));
    await dbUpdate(dbRef(this.db, this.voiceAgentPath(routeName)), { prepMinutes: clamped });
  }
  private _printers: Printer[] | null = null;
  private _internalPrinter = false;

  /**
   * The printer list the UI offers, with the screen's own built-in printer first.
   *
   * It is prepended rather than stored, because it belongs to the device and not to the
   * restaurant: getPrinters() asks the Prime API what printers this sales location has, and
   * the built-in one is not among them - it has no MAC and the API has never heard of it.
   * First, because on a Sunmi screen it is the printer the cook reaches for; and absent on
   * anything without one, so no dead entry is ever offered.
   */
  get printers(): Printer[] | null {
    if (!this._internalPrinter) return this._printers;
    return [internalPrinterEntry(), ...(this._printers || [])];
  }

  get hasInternalPrinter(): boolean {
    return this._internalPrinter;
  }

  /**
   * Re-ask the device whether its built-in printer is reachable.
   *
   * Sunmi's printer service binds asynchronously, so an answer taken during startup can be
   * a false negative that sticks for the rest of the shift. Anything about to show the
   * printer list calls this first rather than trusting what init() found.
   */
  async refreshInternalPrinter(): Promise<boolean> {
    this._internalPrinter = await innerPrinterAvailable();
    return this._internalPrinter;
  }
  set printers(value: Printer[] | null) {
    this._printers = value;
  }

  async getPrinters(): Promise<Printer[]> {
    if (
      !this._receiptApiCredentials ||
      !this._receiptApiCredentials.clientGroupId ||
      !this._receiptApiCredentials.salesLocationId ||
      !this._receiptApiCredentials.apiKey
    ) {
      throw new Error('Cannot fetch printers: missing receipt API credentials');
    }
    const url = `${environment.primeApiUrl}/getPrinters?clientId=${this._receiptApiCredentials.clientGroupId}&salesLocationId=${this._receiptApiCredentials.salesLocationId}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: this._receiptApiCredentials.apiKey,
    };

    // Ensure the result is always an array of Printer, and handle undefined/null
    const result = await this.http.get<Printer[] | undefined>(url, { headers }).toPromise();
    return Array.isArray(result) ? result : [];
  }

  get selectedPrinters(): Printer[] {
    const key = `selectedPrinters_${this.routeName}`;
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  }

  get workingFlags(): WorkingFlags {
    if (!this.routeName) return { ...DEFAULT_WORKING_FLAGS };
    const key = `workingFlags_${this.routeName}`;
    const stored = window.localStorage.getItem(key);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return {
          soundOnBong: typeof parsed.soundOnBong === "boolean" ? parsed.soundOnBong : DEFAULT_WORKING_FLAGS.soundOnBong,
          soundUntilTap: typeof parsed.soundUntilTap === "boolean" ? parsed.soundUntilTap : DEFAULT_WORKING_FLAGS.soundUntilTap,
          textOffOnBong: typeof parsed.textOffOnBong === "boolean" ? parsed.textOffOnBong : DEFAULT_WORKING_FLAGS.textOffOnBong,
          highlightOnNewOrder: typeof parsed.highlightOnNewOrder === "boolean" ? parsed.highlightOnNewOrder : DEFAULT_WORKING_FLAGS.highlightOnNewOrder,
        };
      } catch {
        return { ...DEFAULT_WORKING_FLAGS };
      }
    }
    return { ...DEFAULT_WORKING_FLAGS };
  }

  set workingFlags(flags: WorkingFlags) {
    if (!this.routeName) throw new Error('No routeName to set workingFlags.');
    const key = `workingFlags_${this.routeName}`;
    window.localStorage.setItem(key, JSON.stringify(flags));
  }
}

/**
 * The built-in printer as a Printer row, for a list built for networked printers.
 *
 * printerMac is empty because it has none, which is also how the printers dialog knows to
 * label it rather than print a MAC under it, and why the dialog falls back to id when it
 * reports which printer was chosen. name is empty for the same reason: the dialog
 * translates it, so the label follows the screen's language rather than whatever the
 * language was when the list was built.
 */
export function internalPrinterEntry(): Printer {
  return {
    id: INTERNAL_PRINTER_ID,
    name: '',
    printerMac: '',
    createdUtc: '',
    timestamp: 0,
  };
}
